import React from 'react';
import styled from 'styled-components';
import { sectionShell, sectionHeader, sectionTitle, sectionDescription } from '../../styles/sectionStyles';
import { motion } from 'framer-motion';
import { FaCode, FaEye, FaLaptopCode, FaNetworkWired, FaUniversalAccess, FaUsers } from 'react-icons/fa';
import { leadership, research } from '../../data/constants';
import { demoLabel } from '../../utils/projectHelpers';

const focusIcons = [FaEye, FaLaptopCode, FaCode, FaNetworkWired, FaUniversalAccess];

const Container = styled.section`
  ${sectionShell}
`;

const Wrapper = styled.div`
  width: min(1180px, 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 34px;
`;

const Header = styled.div`
  ${sectionHeader}
`;

const Eyebrow = styled.div`
  color: var(--accent);
  font-size: 12px;
  font-weight: 650;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin-bottom: 12px;
`;

const Title = styled.h2`
  ${sectionTitle}
`;

const Accent = styled.span`
  color: var(--accent);
`;

const Desc = styled.p`
  ${sectionDescription}
`;

const Grid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(320px, 0.65fr);
  gap: 26px;
  align-items: start;

  @media (max-width: 940px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.article`
  padding: 28px;
  border-radius: 18px;
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-card);

  @media (max-width: 560px) {
    padding: 22px;
  }
`;

const ResearchTitle = styled.h3`
  margin: 0;
  color: var(--text);
  font-size: 24px;
  line-height: 1.24;
  font-weight: 650;
`;

const Text = styled.p`
  margin: 16px 0 0;
  color: var(--muted);
  font-size: 16px;
  line-height: 1.75;
  font-weight: 400;
`;

const FocusGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 22px;
`;

const FocusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 8px 12px;
  border-radius: 999px;
  color: var(--text);
  background: var(--surface);
  border: 1px solid var(--border);
  font-size: 13px;
  font-weight: 650;

  svg {
    color: var(--accent);
  }
`;

const SideTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 20px;
  color: var(--text);
  font-size: 20px;
  font-weight: 650;

  svg {
    color: var(--accent);
  }
`;

const LeadList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const LeadItem = styled.div`
  padding: 16px;
  border-radius: 14px;
  background: var(--surface);
  border: 1px solid var(--border);
`;

const Role = styled.h4`
  margin: 0;
  color: var(--text);
  font-size: 15px;
  font-weight: 650;
`;

const Org = styled.div`
  margin-top: 6px;
  color: var(--accent);
  font-size: 13px;
  font-weight: 650;
`;

const Research = () => {
  const item = research[0];

  return (
    <Container id="research">
      <Wrapper>
        <Header as={motion.div} initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.5, ease: 'easeOut' }}>
          <Eyebrow>Research & Leadership</Eyebrow>
          <Title>Research <Accent>&amp;</Accent> Leadership</Title>
          <Desc>My completed assistive communication research and contributions to university technical communities.</Desc>
        </Header>

        <Grid>
          <Panel as={motion.article} initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.5, ease: 'easeOut' }}>
            <ResearchTitle>{item.title}</ResearchTitle>
            <Text>{item.desc}</Text>
            <SideTitle as="h4" style={{ marginTop: 24 }}>Research Problem</SideTitle>
            <Text>{item.problem}</Text>
            <SideTitle as="h4" style={{ marginTop: 24 }}>What I Built</SideTitle>
            <Text>{item.solution}</Text>
            <FocusGrid>
              {item.focus.map((focus, index) => {
                const Icon = focusIcons[index % focusIcons.length];
                return <FocusPill key={focus}><Icon /> {focus}</FocusPill>;
              })}
            </FocusGrid>
            <SideTitle as="h4" style={{ marginTop: 24 }}>Communication & Control</SideTitle>
            <Text>{item.implementation}</Text>
            {item.methodology && <><SideTitle as="h4" style={{ marginTop: 24 }}>Healthy-Volunteer Evaluation</SideTitle><Text>{item.methodology}</Text></>}
            {item.results && <><SideTitle as="h4" style={{ marginTop: 24 }}>Academic Achievement</SideTitle><Text>{item.results}</Text></>}
            {item.screenshots?.map(shot => <figure key={shot.src}><img src={shot.src} alt={shot.alt} loading="lazy" decoding="async" /><figcaption>{shot.alt}</figcaption></figure>)}
            {item.githubLink && <Text as="a" href={item.githubLink} target="_blank" rel="noopener noreferrer">Research Repository</Text>}
            {demoLabel(item.demoLink) && <Text as="a" href={item.demoLink} target="_blank" rel="noopener noreferrer">{demoLabel(item.demoLink)}</Text>}
          </Panel>

          <Panel as={motion.article} initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.5, delay: 0.06, ease: 'easeOut' }}>
            <SideTitle><FaUsers /> Leadership</SideTitle>
            <LeadList>
              {leadership.map((item) => (
                <LeadItem key={item.id}>
                  <Role>{item.role}</Role>
                  <Org>{item.organization}</Org>
                  <Text>{item.desc}</Text>
                </LeadItem>
              ))}
            </LeadList>
          </Panel>
        </Grid>
      </Wrapper>
    </Container>
  );
};

export default Research;
